import React, { useMemo } from "react";
import styled from "styled-components";
import { Icon } from "@opendash/icons";

import { AdminToolbar } from "@opendash/ui";
import { AdminLayout, LinkedSourcePicker } from "@opendash/core";

import { Alert, Button, Empty, Result } from "antd";

import { useTranslation, copy } from "@opendash/core";

import GreisTransponderTable from "./GreisTransponderTable";
import GreisTransponderImport from "./GreisTransponderImport";
import GreisTransponderExport from "./GreisTransponderExport";
import GreisTransponderAdd from "./GreisTransponderAdd";
import GreisTransponderDedupe from "./GreisTransponderDedupe";

const ButtonGroup = styled.div`
  display: flex;
  margin-bottom: 24px;
  justify-content: flex-end;

  > button {
    margin-left: 8px;
  }
`;

// Types
interface TransponderRow {
  id: string;
  misc?: string;
  misc2?: string;
  owner?: string;
  active?: boolean;
  _deleted?: boolean;
}

type UpdatableKeys = "misc" | "misc2" | "owner" | "active" | "_deleted";

interface DataObjectLike {
  get: (key: string) => unknown;
  set: (key: string, value: unknown) => DataObjectLike;
  save: () => Promise<unknown>;
}

interface TransponderConfigLike {
  result: DataObjectLike | null;
  loading: boolean;
  error?: unknown;
}

interface Props {
  spsid?: string;
  transponderConfig: TransponderConfigLike;
  admin?: boolean;
}

export default function GreisTransponderConfig({
  spsid,
  transponderConfig,
  admin,
}: Props) {
  const { result, loading, error } = transponderConfig;

  const t = useTranslation();
  const [transponders, setTransponders] = React.useState<TransponderRow[]>([]);
  const [importModal, setImportModal] = React.useState(false);
  const [exportModal, setExportModal] = React.useState(false);
  const [addModal, setAddModal] = React.useState(false);
  const [dedupeModal, setDedupeModal] = React.useState(false);

  React.useEffect(() => {
    if (result) {
      setTransponders(copy(result.get("cardIDs")) as TransponderRow[]);
    } else {
      setTransponders([]);
    }
  }, [result]);

  const changes = useMemo(() => {
    let count = 0;
    let countNew = 0;
    let countDeleted = 0;

    const sourceCardIDs: TransponderRow[] =
      (result?.get("cardIDs") as TransponderRow[] | undefined) ?? [];

    // Lookup-Map statt find() pro Zeile, sonst O(n²) bei vielen Einträgen.
    // Pro ID alle Zeilen in Reihenfolge, damit bei doppelten IDs die n-te Zeile
    // gegen die n-te alte Zeile verglichen wird und nicht gegen die erste.
    const sourceById = new Map<string, TransponderRow[]>();

    for (const row of sourceCardIDs) {
      const rows = sourceById.get(row.id);

      if (rows) {
        rows.push(row);
      } else {
        sourceById.set(row.id, [row]);
      }
    }

    const usedById = new Map<string, number>();

    for (const t of transponders) {
      const used = usedById.get(t.id) ?? 0;
      const oldVersion = sourceById.get(t.id)?.[used];

      if (oldVersion) {
        usedById.set(t.id, used + 1);
      }

      if (t._deleted) {
        count++;
        countDeleted++;
        continue;
      }

      if (!oldVersion) {
        count++;
        countNew++;
        continue;
      }

      const diff = JSON.stringify(t) !== JSON.stringify(oldVersion);

      if (diff) {
        count++;
      }
    }

    return { count, countNew, countDeleted };
  }, [transponders]);

  const saveTransponders = () => {
    const toSave = transponders
      .filter((x) => !x._deleted)
      .map((x) => ({
        id: x.id,
        misc: x.misc,
        misc2: x.misc2,
        owner: x.owner,
        active: x.active,
      }));

    if (!result) return;
    const r = result; // narrow for async callbacks

    r.set("cardIDs", toSave)
      .save()
      .then(() => {
        setTransponders(copy(r.get("cardIDs")) as TransponderRow[]);
      });
  };

  const addTransponders = (arr: TransponderRow[]) => {
    setTransponders([...transponders, ...arr]);
  };

  const overwriteTransponders = (arr: TransponderRow[]) => {
    setTransponders([...arr]);
  };

  function updateTransponder<K extends UpdatableKeys>(
    id: string,
    key: K,
    value: TransponderRow[K]
  ) {
    setTransponders(
      transponders.map((row) => {
        if (row.id === id) {
          return {
            ...row,
            [key]: value,
          } as TransponderRow;
        }

        return row;
      })
    );
  }

  if (!spsid) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "70vh", // Mindesthöhe, um den Inhalt zu zentrieren
        }}
      >
        <Result
          icon={
            <Icon
              icon="fa:circle"
              style={{ color: "rgb(254, 192, 0)", fontSize: "48px" }}
            />
          }
          title={t("greis:transponders.admin.select_location_first")}
          extra={
            <div>
              <p style={{ marginBottom: "8px", fontSize: "16px" }}>
                {t("greis:transponders.admin.select_comp_location")}
              </p>
              <LinkedSourcePicker style={{ width: "400px" }} />
            </div>
          }
        />
      </div>
    );
  }

  const getAction = (isAdmin: boolean) => {
    if (
      spsid &&
      !spsid.includes("tenant") &&
      !(!transponderConfig.loading && !transponderConfig.result)
    ) {
      if (isAdmin) {
        return [
          <Button
            type="primary"
            disabled={changes.count === 0}
            onClick={(e) => {
              saveTransponders();
            }}
            children={t("greis:transponders.admin.save")}
          />,

          <Button
            icon={<Icon icon="fa:download" />}
            disabled={loading}
            onClick={(e) => {
              setImportModal(true);
            }}
            children={t("greis:transponders.admin.import_button")}
          />,
          <Button
            icon={<Icon icon="fa:download" />}
            disabled={loading}
            onClick={(e) => {
              setExportModal(true);
            }}
            children={t("greis:transponders.admin.export_button")}
          />,
          <Button
            icon={<Icon icon="fa:plus" />}
            disabled={loading}
            onClick={(e) => {
              setAddModal(true);
            }}
            children={t("greis:transponders.admin.add_button")}
          />,
          <Button
            icon={<Icon icon="fa:clone" />}
            disabled={loading}
            onClick={(e) => {
              setDedupeModal(true);
            }}
            children={t("greis:transponders.admin.dedupe_button")}
          />,
        ];
      } else {
        return [
          <Button
            type="primary"
            disabled={changes.count === 0}
            onClick={(e) => {
              saveTransponders();
            }}
            children={t("greis:transponders.admin.save")}
          />,
        ];
      }
    } else {
      return [];
    }
  };

  return (
    <AdminLayout>
      <AdminToolbar
        title={t("greis:transponders.admin.select_location")}
        description={t("greis:transponders.admin.trans_description")}
        actions={getAction(!!admin)}
      />
      <div className="od-page-main">
        {((spsid && spsid.includes("tenant")) || !transponderConfig.result) && (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              minHeight: "50vh", // Mindesthöhe, um den Inhalt zu zentrieren
            }}
          >
            <Result
              icon={
                <Icon
                  icon="fa:circle"
                  style={{ color: "rgb(254, 192, 0)", fontSize: "48px" }}
                />
              }
              title={t("greis:manualinput.component.no_support")}
              extra={
                <div>
                  <p style={{ marginBottom: "8px", fontSize: "16px" }}>
                    {t("greis:manualinput.component.select_location")}
                  </p>
                  <LinkedSourcePicker style={{ width: "400px" }} />
                </div>
              }
            />
          </div>
        )}

        {spsid && !spsid.includes("tenant") && !!transponderConfig.result && (
          <>
            {changes.count > 0 && (
              <Alert
                type="info"
                message={t("greis:transponders.admin.changes", changes)}
                style={{ marginBottom: 24 }}
              />
            )}

            <GreisTransponderImport
              open={importModal}
              close={() => {
                setImportModal(false);
              }}
              transponders={transponders}
              addTransponders={addTransponders}
              overwriteTransponders={overwriteTransponders}
            />

            <GreisTransponderExport
              open={exportModal}
              close={() => {
                setExportModal(false);
              }}
              transponders={transponders}
            />

            <GreisTransponderAdd
              open={addModal}
              close={() => {
                setAddModal(false);
              }}
              addTransponders={addTransponders}
            />

            <GreisTransponderDedupe
              open={dedupeModal}
              close={() => {
                setDedupeModal(false);
              }}
              transponders={transponders}
              overwriteTransponders={overwriteTransponders}
            />

            <GreisTransponderTable
              {...{ transponders, updateTransponder, loading, admin }}
            />
          </>
        )}
      </div>
    </AdminLayout>
  );
}
